import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useLazySearchDownlineByUserIdQuery } from '../services/auth/authApi';
import { useGetLedgerByUserIdQuery, useCreateCashTransactionMutation } from '../services/ledger/ledgerApi';

// Down Arrow Icon
const DownArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z"></path>
  </svg>
);


const DebitCreditEntryPage = ({ userType = 1, title = "Client Transactions" }) => {
  const navigate = useNavigate();
  
  // Map userType number to string
  const userTypeMapping = {
    1: 'Client',
    2: 'Agent', 
    3: 'Super',
    4: 'Master',
    5: 'MiniAdmin',
    7: 'SuperAdmin'
  };
  
  const userTypeString = userTypeMapping[userType] || 'Client';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchTimeoutRef = useRef(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserDisplay, setSelectedUserDisplay] = useState(''); // Store display name

  // RTK Query hooks
  const [createCashTransaction, { isLoading: submitting }] = useCreateCashTransactionMutation();
  const [searchDownline, { isLoading: searching }] = useLazySearchDownlineByUserIdQuery();
  const { data: ledgerData, isLoading: loadingLedger, refetch: refetchLedger } = useGetLedgerByUserIdQuery(
    { userId: selectedUserId },
    { skip: !selectedUserId }
  );

  const transactions = ledgerData?.data || [];

  // Form state
  const [formData, setFormData] = useState({
    client: '',
    collection: 'CASH A/C',
    amount: '',
    paymentType: '',
    remark: ''
  });

  // Dropdown states
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [collectionDropdownOpen, setCollectionDropdownOpen] = useState(false);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);

  // Collection options
  const collectionOptions = ['CASH A/C', 'BANK A/C', 'OTHER'];

  // Payment type options
  const paymentTypeOptions = ['PAYMENT - DIYA', 'RECEIPT - LIYA'];

  // Calculate totals
  const totals = transactions.reduce((acc, txn) => {
    const debit = parseFloat(txn.debit) || 0;
    const credit = parseFloat(txn.credit) || 0;
    acc.dena += debit;
    acc.lena += credit;
    return acc;
  }, { dena: 0, lena: 0 });

  const balance = totals.dena - totals.lena;

  const handleSearch = async (value) => {
    setSearchQuery(value);
    
    // Clear selected user display when user starts typing
    if (value && selectedUserDisplay) {
      setSelectedUserDisplay('');
      setFormData({ ...formData, client: '' });
      setSelectedUserId(null);
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchDownline({ userId: value, userType: userTypeString }).unwrap();
        if (response.success && response.data) {
          setSearchResults(response.data);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        setSearchResults([]);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.client) {
      message.error("Please select a client");
      return;
    }
    if (!formData.amount) {
      message.error("Please enter amount");
      return;
    }
    if (!formData.paymentType) {
      message.error("Please select payment type");
      return;
    }

    try {
      // Always use current date and time (ignore selected date)
      const now = new Date();
      
      const submitData = {
        userId: formData.client,
        amount: parseFloat(formData.amount),
        collection: formData.collection,
        paymentType: formData.paymentType,
        date: now.toISOString(),
        remark: formData.remark
      };

      const response = await createCashTransaction(submitData).unwrap();

      if (response.status === true || response.success) {
        // toast.success("Transaction created successfully");
        
        // Refetch ledger data
        if (selectedUserId) {
          refetchLedger();
        }

        // Reset form but keep client selected
        setFormData({
          ...formData,
          amount: '',
          paymentType: '',
          remark: ''
        });
        // Don't clear searchQuery, selectedUserId, or selectedUserDisplay
      } else {
        // message.error(response.message || "Failed to create transaction");
      }
    } catch (err) {
      // message.error(err.data?.message || "Failed to create transaction");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDelete = () => {
    setSelectedUserId(null);
    setFormData({ ...formData, client: '' });
    setSearchQuery('');
    setSelectedUserDisplay('');
    // message.success("Ledger cleared");
  };

  return (
    <div className="w-full">
      <div className="bg-white shadow rounded">
        {/* Header */}
        <div className="bg-[#1F2937] px-3 sm:px-5 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <span className="text-lg sm:text-xl text-white font-normal break-words">{title}</span>
          <button
            onClick={handleBack}
            className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 font-medium w-full sm:w-auto"
          >
            Back
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-2 sm:p-6 lg:p-8 bg-[var(--surface)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* Client Dropdown */}
            <div className="relative">
              <label className="block mb-2 text-sm">
                <span className="text-red-500">*</span> Client
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type to search..."
                  value={searchQuery !== '' ? searchQuery : selectedUserDisplay}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setClientDropdownOpen(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-white hover:border-blue-500 focus:border-blue-500 focus:outline-none"
                />
                {clientDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-auto">
                    {searching ? (
                      <div className="px-3 py-2 text-gray-500">Searching...</div>
                    ) : searchQuery ? (
                      searchResults.length === 0 ? (
                        <div className="px-3 py-2 text-gray-500">No results found</div>
                      ) : (
                        searchResults.map((client) => (
                          <div
                            key={client.id}
                            onClick={() => {
                              setFormData({ ...formData, client: client.id });
                              setSelectedUserId(client.id);
                              setSelectedUserDisplay(`${client.userId} (${client.fullname || client.fullName || ''})`);
                              setSearchQuery('');
                              setClientDropdownOpen(false);
                            }}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                          >
                            {client.userId} ({client.fullname || client.fullName || ''})
                          </div>
                        ))
                      )
                    ) : (
                      <div className="px-3 py-2 text-gray-500">Type to search clients...</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Collection Dropdown */}
            <div className="relative">
              <label className="block mb-2 text-sm">
                <span className="text-red-500">*</span> Collection
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCollectionDropdownOpen(!collectionDropdownOpen)}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-white text-left flex items-center justify-between hover:border-blue-500 focus:border-blue-500 focus:outline-none"
                >
                  <span className="text-gray-900">{formData.collection}</span>
                  <DownArrowIcon />
                </button>
                {collectionDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {collectionOptions.map((option) => (
                      <div
                        key={option}
                        onClick={() => {
                          setFormData({ ...formData, collection: option });
                          setCollectionDropdownOpen(false);
                        }}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* Amount Input */}
            <div>
              <label className="block mb-2 text-sm">
                <span className="text-red-500">*</span> Amount
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded bg-white placeholder-gray-400 hover:border-blue-500 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Payment Type Dropdown */}
            <div className="relative">
              <label className="block mb-2 text-sm">
                <span className="text-red-500">*</span> Payment Type
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPaymentDropdownOpen(!paymentDropdownOpen)}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-white text-left flex items-center justify-between hover:border-blue-500 focus:border-blue-500 focus:outline-none"
                >
                  <span className={formData.paymentType ? "text-gray-900" : "text-gray-400"}>
                    {formData.paymentType || "Payment Type"}
                  </span>
                  <DownArrowIcon />
                </button>
                {paymentDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {paymentTypeOptions.map((option) => (
                      <div
                        key={option}
                        onClick={() => {
                          setFormData({ ...formData, paymentType: option });
                          setPaymentDropdownOpen(false);
                        }}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Remark Input */}
            <div>
              <label className="block mb-2 text-sm">
                <span className="text-red-500">*</span> Remark
              </label>
              <input
                type="text"
                placeholder="Remarks"
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded bg-white placeholder-gray-400 hover:border-blue-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-500 text-white px-8 py-2 rounded hover:bg-blue-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>

        {/* Transaction Summary and Table */}
        {selectedUserId && (
          <div className="mt-4 sm:mt-6 p-2 sm:p-6 bg-white">
            {/* Summary Row */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-4 px-2 sm:px-4 py-2">
              <div className="text-red-600 text-base sm:text-lg font-medium">
                Dena : {ledgerData?.dena || 0}
              </div>
              <div className="text-green-600 text-base sm:text-lg font-medium">
                Lena : {ledgerData?.lena || 0}
              </div>
              <div className={`text-base sm:text-lg font-medium ${(ledgerData?.netBalance ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                Balance: {ledgerData?.netBalance != null ? Math.abs(ledgerData.netBalance).toFixed(2) : '0.00'} ({(ledgerData?.netBalance ?? 0) >= 0 ? 'Lena' : 'Dena'})
              </div>
              {/* <button
                onClick={handleDelete}
                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
              >
                Deleted
              </button> */}
            </div>

            {/* Transaction Table */}
            <div className="overflow-x-auto">
              <table className="ledger-table w-full border-collapse text-xs sm:text-sm">
                <thead className="bg-[#1F2937] text-white">
                  <tr>
                    <th className="border border-gray-600 text-left whitespace-nowrap" style={{ padding: '6px 8px' }}>#</th>
                    <th className="border border-gray-600 text-left whitespace-nowrap" style={{ padding: '6px 8px' }}>Date</th>
                    {/* <th className="border border-gray-600 text-left" style={{ padding: '6px 8px' }}>Post Date</th> */}
                    {/* <th className="border border-gray-600 text-left" style={{ padding: '6px 8px' }}>Collection Name</th> */}
                    <th className="border border-gray-600 text-left whitespace-nowrap" style={{ padding: '6px 8px' }}>Debit</th>
                    <th className="border border-gray-600 text-left whitespace-nowrap" style={{ padding: '6px 8px' }}>Credit</th>
                    <th className="border border-gray-600 text-left whitespace-nowrap" style={{ padding: '6px 8px' }}>Balance</th>
                    <th className="border border-gray-600 text-left whitespace-nowrap" style={{ padding: '6px 8px' }}>Payment Type</th>
                    <th className="border border-gray-600 text-left whitespace-nowrap" style={{ padding: '6px 8px' }}>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingLedger ? (
                    <tr>
                      <td colSpan="10" className="border border-gray-300 px-4 py-8 text-center text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="border border-gray-300 px-4 py-8 text-center text-gray-400">
                        <div className="flex flex-col items-center">
                          <svg className="w-16 h-16 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                          <p>No Data</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    transactions.map((txn, index) => {
                      const pt = (txn.paymentType || '').toUpperCase();
                      const rowType = pt.includes('LIYA') ? 'ledger-liya' : pt.includes('DIYA') ? 'ledger-diya' : '';
                      return (
                      <tr key={index} className={`bg-white hover:bg-gray-50 ${rowType}`}>
                        <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.no || index + 1}</td>
                        <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.date || '-'}</td>
                        {/* <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.postDate || '-'}</td> */}
                        {/* <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.collectionName || '-'}</td> */}
                        <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.debit || ''}</td>
                        <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.credit || ''}</td>
                        <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.balance || '-'}</td>
                        <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.paymentType || '-'}</td>
                        <td className="border border-gray-300 whitespace-nowrap" style={{ padding: '6px 8px', fontSize: '14px', fontWeight: 700 }}>{txn.remark || '-'}</td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebitCreditEntryPage;